/**
 * Error trigger catalogue for the Story 307 demo page.
 *
 * Each trigger fabricates a representative library error so the page can
 * render every Pajamas error variant the rest of the app uses. The
 * triggers are deterministic — no real network calls — keeping the page
 * useful as a visual QA reference.
 *
 * Per the AppSec note on issue #19, every error message is run through
 * `maskSecrets` before being attached to the trigger so a stray API key
 * baked into the demo cannot leak into the rendered UI. The
 * `maskSecrets` helper is available from the shared library.
 */
import {
  AuthError,
  ConfigError,
  ConnectionError,
  RateLimitError,
  TimeoutError,
  maskSecrets,
} from '@gl-demo-ultimate-odupre/third-party-connections-shared';

export type ErrorVariant =
  | 'auth'
  | 'rate-limit'
  | 'timeout'
  | 'config'
  | 'connection';

export interface ErrorTrigger {
  readonly id: ErrorVariant;
  readonly label: string;
  readonly description: string;
  /** Stretch goal — true on triggers that recover automatically (rate-limit). */
  readonly recoverable: boolean;
  fire(): ConnectionError;
}

const PLAUSIBLE_KEY = 'sk-ant-api03-EXAMPLE-IGNORE-ME-1234567890';

/**
 * Pattern matching Anthropic-style API keys. `maskSecrets` from the
 * shared library masks sensitive *object keys* (e.g. `apiKey`, `token`),
 * not raw string substrings. This page demonstrates errors with
 * messages that may embed a key, so we apply a defence-in-depth string
 * scrub here in addition to the object-level mask.
 */
const ANTHROPIC_KEY_PATTERN = /sk-ant-[A-Za-z0-9_-]+/g;

/**
 * Apply both string-level and object-level scrubbing.
 *
 * 1. Replace every `sk-ant-...` substring with `***` (regex).
 * 2. Wrap the result in `{ message }` and run `maskSecrets` so any
 *    future field renames in the shared library propagate without
 *    needing a code change here.
 */
function scrubMessage(input: string): string {
  const stringScrubbed = input.replace(ANTHROPIC_KEY_PATTERN, '***');
  const masked = maskSecrets({ message: stringScrubbed }) as { message: string };
  return masked.message;
}

export const errorTriggers: ReadonlyArray<ErrorTrigger> = [
  {
    id: 'auth',
    label: 'Authentication failure',
    description:
      'HTTP 401 / 403 — the caller must produce fresh credentials. Not retryable.',
    recoverable: false,
    fire(): ConnectionError {
      return new AuthError({
        message: scrubMessage(
          `authentication failed: invalid api key (preview: ${PLAUSIBLE_KEY})`,
        ),
        provider: 'claude',
      });
    },
  },
  {
    id: 'rate-limit',
    label: 'Rate limited (429)',
    description:
      'HTTP 429 — Anthropic asked us to back off. Always retryable; the demo shows the requested delay and a Retry button that simulates withBackoff.',
    recoverable: true,
    fire(): ConnectionError {
      return new RateLimitError({
        message: scrubMessage('429 Too Many Requests — slow down'),
        provider: 'claude',
        retryAfterMs: 1500,
      });
    },
  },
  {
    id: 'timeout',
    label: 'Request timeout',
    description:
      'Operation exceeded its 30 s deadline. Retryable with a longer budget.',
    recoverable: false,
    fire(): ConnectionError {
      return new TimeoutError({
        message: scrubMessage('request exceeded 30000ms'),
        provider: 'claude',
        timeoutMs: 30000,
      });
    },
  },
  {
    id: 'config',
    label: 'Missing required field',
    description:
      'Configuration validation failed — auth.key is required. Not retryable until the caller fixes the config.',
    recoverable: false,
    fire(): ConnectionError {
      return new ConfigError({
        message: scrubMessage('auth.key is required'),
        provider: 'claude',
        field: 'auth.key',
        expected: 'non-empty string starting with "sk-ant-"',
        validationErrors: ['auth.key: Required'],
      });
    },
  },
  {
    id: 'connection',
    label: 'Generic upstream error (5xx)',
    description:
      'HTTP 500-599 — Anthropic returned an unrecoverable upstream error. Retryable with the standard backoff schedule.',
    recoverable: true,
    fire(): ConnectionError {
      return new ConnectionError({
        message: scrubMessage('upstream error 503 Service Unavailable'),
        code: 'UPSTREAM_ERROR',
        retryable: true,
        provider: 'claude',
      });
    },
  },
];

export function findTrigger(id: ErrorVariant): ErrorTrigger | undefined {
  return errorTriggers.find((t) => t.id === id);
}

/**
 * Stretch goal — simulate `withBackoff` retrying after the rate-limit's
 * `retryAfterMs`. Returns a Promise that resolves with the round count
 * after the requested delay. The demo page uses a short multiplier so
 * the visual feedback is fast.
 */
export interface BackoffSimulation {
  readonly retryAfterMs: number;
  readonly attempts: number;
}

export async function simulateRateLimitRecovery(
  err: RateLimitError,
  options: { speedupFactor?: number; setTimeoutImpl?: typeof setTimeout } = {},
): Promise<BackoffSimulation> {
  const speedupFactor = options.speedupFactor ?? 1;
  const setTimeoutImpl = options.setTimeoutImpl ?? setTimeout;
  const wait = Math.max(0, Math.round(err.retryAfterMs / speedupFactor));
  await new Promise<void>((resolve) =>
    setTimeoutImpl(() => resolve(), wait),
  );
  return { retryAfterMs: err.retryAfterMs, attempts: 1 };
}
